import { Select, SelectItem } from "@heroui/react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

const cn = (...inputs) => twMerge(clsx(inputs));

/** Normalize option key for HeroUI */
function optionKey(item) {
  if (item == null) return "";
  const k = item.key ?? item.value;
  return k != null ? String(k) : "";
}

export const COMMON_SELECT_CREATE_NEW_KEY = "__common_select_create_new__";

const CommonSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  ariaLabel,
  width = "w-[256px]",
  startContent,
  endContent,
  disabled = false,
  onCreateNew,
  createNewLabel = "Create new Campaign",
  classNames: classNamesProp = {},
  popoverMatchTriggerWidth = false,
  listboxProps: listboxPropsProp = {},
  bottomContent: bottomContentProp,
}) => {
  const selectedKey =
    typeof value === "string" || typeof value === "number"
      ? String(value)
      : value?.value != null
        ? String(value.value)
        : "";

  const selectedKeys = selectedKey !== "" ? new Set([selectedKey]) : new Set();

  const handleSelectionChange = (keys) => {
    if (typeof onChange !== "function") return;
    if (keys === "all") return;
    const selected = [...keys][0];
    const keyStr = selected != null ? String(selected) : "";
    if (onCreateNew && keyStr === COMMON_SELECT_CREATE_NEW_KEY) {
      onCreateNew();
      return;
    }
    const opt = options?.find((o) => optionKey(o) === keyStr);
    if (opt?.disabled) return;
    onChange(selected ?? null);
  };

  const newActionBottomContent = bottomContentProp ?? (onCreateNew ? (
    <div className="pt-1.5 mt-1 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 sticky bottom-0 z-20">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCreateNew();
          document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
        }}
        className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-secondary hover:bg-secondary/10 dark:hover:bg-secondary/20 rounded-lg transition-colors cursor-pointer text-left"
      >
        {createNewLabel}
      </button>
    </div>
  ) : null);

  const mergedClassNames = {
    base: cn(
      "border-none shadow-none bg-transparent min-w-0",
      width,
      classNamesProp.base,
    ),
    mainWrapper: cn("w-full rounded-lg", classNamesProp.mainWrapper),
    trigger: cn(
      width,
      "min-w-0 max-w-full h-9 min-h-8 px-2 py-0 gap-1 text-[0.815rem] font-inter text-gray-800 dark:text-gray-100 rounded-lg",
      "border border-gray-300 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xs transition-[background-color,border-color]",
      "hover:bg-gray-50 dark:hover:bg-gray-800 data-[hover=true]:bg-gray-50 dark:data-[hover=true]:bg-gray-800",
      "data-[open=true]:border-secondary data-[open=true]:border-[1.5px]",
      "data-[focus=true]:border-secondary data-[focus=true]:border-[1.5px]",
      "focus-within:border-secondary focus-within:border-[1.5px]",
      "outline-none ring-0 data-[pressed=true]:scale-100",
      "[&[data-placeholder=true]_[data-slot=value]]:text-gray-400 dark:[&[data-placeholder=true]_[data-slot=value]]:text-gray-500",
      disabled && "opacity-50 cursor-not-allowed",
      classNamesProp.trigger,
    ),
    innerWrapper: cn(
      "w-full min-w-0 max-w-full overflow-hidden pr-6 font-semibold",
      classNamesProp.innerWrapper,
    ),
    value: cn(
      "font-inter !text-[0.815rem] truncate block text-left",
      "!text-gray-800 dark:!text-gray-100 !font-medium",
      "data-[placeholder=true]:!text-gray-400 dark:data-[placeholder=true]:!text-gray-500 data-[placeholder=true]:!font-normal",
      classNamesProp.value,
    ),
    selectorIcon: cn(
      "flex-shrink-0 text-gray-600 dark:text-gray-400 font-medium",
      classNamesProp.selectorIcon,
    ),
    popoverContent: cn(
      "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-1.5 shadow-lg rounded-xl",
      popoverMatchTriggerWidth || width === "w-full"
        ? "w-full min-w-full"
        : "min-w-[160px] w-max",
      classNamesProp.popoverContent,
    ),
    listbox: classNamesProp.listbox,
    // With a bottom action, only the options scroll (see listboxProps) so the action stays
    // visible however long the list is.
    listboxWrapper: cn(
      newActionBottomContent ? "max-h-none overflow-visible" : "max-h-[260px] overflow-y-auto",
      classNamesProp.listboxWrapper,
    ),
  };

  const accessibilityLabel =
    ariaLabel ?? (typeof placeholder === "string" ? placeholder : "Select");

  return (
    <Select
      size="sm"
      aria-label={accessibilityLabel}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      placeholder={placeholder}
      disallowEmptySelection={selectedKeys.size > 0}
      isDisabled={disabled}
      classNames={mergedClassNames}
      startContent={startContent}
      endContent={endContent}
      popoverProps={{
        style: { zIndex: 100000001 },
      }}
      listboxProps={{
        ...listboxPropsProp,
        bottomContent: newActionBottomContent,
        classNames: {
          ...listboxPropsProp?.classNames,
          list: cn(
            newActionBottomContent && "max-h-[220px] overflow-y-auto",
            listboxPropsProp?.classNames?.list,
          ),
        },
      }}
    >
      {options?.map((item) => {
        const k = optionKey(item);
        return (
          <SelectItem
            key={k}
            textValue={item.label}
            isDisabled={!!item.disabled}
            startContent={item.startContent}
            classNames={{
              base: cn(
                "px-3 py-2 text-xs font-medium text-gray-800 dark:text-gray-100 rounded-lg cursor-pointer transition-colors min-w-full",
                popoverMatchTriggerWidth || width === "w-full"
                  ? "w-full"
                  : "whitespace-nowrap",
                "data-[hover=true]:!bg-gray-100 dark:data-[hover=true]:!bg-gray-800 data-[hover=true]:!text-secondary dark:data-[hover=true]:!text-secondary-light",
                "data-[selectable=true]:focus:bg-gray-100 dark:data-[selectable=true]:focus:bg-gray-800",
                "data-[selected=true]:!bg-gray-100 dark:data-[selected=true]:!bg-gray-800 data-[selected=true]:!font-semibold",
                "data-[focus-visible=true]:!outline-none",
              ),
              title: cn(
                "text-xs font-medium",
                popoverMatchTriggerWidth || width === "w-full"
                  ? "truncate w-full block"
                  : "whitespace-nowrap overflow-visible",
              ),
            }}
          >
            {item.label}
          </SelectItem>
        );
      })}
    </Select>
  );
};

export default CommonSelect;
